import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { sanitizeRedirect } from "../../lib/redirect";
import AuthShell from "./AuthShell";
import PrivacyPolicyModal from "../../components/PrivacyPolicyModal";

function formatError(detail) {
  if (!detail) return "Não foi possível concluir. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(" ");
  return detail.msg || String(detail);
}

// BR phone mask: (XX) XXXXX-XXXX or (XX) XXXX-XXXX
function maskPhone(raw) {
  const d = (raw || "").replace(/\D/g, "").slice(0, 11);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

// BR CPF mask + checksum
function maskCpf(raw) {
  const d = (raw || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}
function isValidCpf(raw) {
  const c = (raw || "").replace(/\D/g, "");
  if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;
  for (const j of [9, 10]) {
    let total = 0;
    for (let i = 0; i < j; i++) total += parseInt(c[i], 10) * (j + 1 - i);
    let d = (total * 10) % 11;
    if (d === 10) d = 0;
    if (d !== parseInt(c[j], 10)) return false;
  }
  return true;
}

export default function RegisterPage() {
  const { login, registerEmail } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = sanitizeRedirect(params.get("redirect"));
  // Guest checkout → "Criar conta" CTA passes name/email/phone/cpf via query
  // string so the buyer doesn't retype anything. Values are treated as
  // best-effort seeds; the user can still edit any field.
  const [form, setForm] = useState({
    name: params.get("name") || "",
    email: params.get("email") || "",
    phone: params.get("phone") || "",
    cpf: params.get("cpf") ? maskCpf(params.get("cpf")) : "",
    password: "",
    confirm: "",
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("A senha precisa ter no mínimo 8 caracteres.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("As senhas não coincidem.");
      return;
    }
    if (!isValidCpf(form.cpf)) {
      setError("CPF inválido. Verifique os 11 dígitos.");
      return;
    }
    if (!privacyAccepted) {
      // Belt-and-braces guard — the submit button is also disabled when this is false.
      setError("É necessário aceitar a Política de Privacidade para criar a conta.");
      return;
    }
    setLoading(true);
    try {
      await registerEmail({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone,
        cpf: form.cpf.replace(/\D/g, ""),
        password: form.password,
        privacyPolicyAccepted: true,
      });
      // Preserve the post-login destination through the email verification step
      // so once the user clicks the confirmation link they end up back on the
      // page they came from (e.g. the PDP they were viewing).
      const redirectQS = redirect !== "/" ? `&redirect=${encodeURIComponent(redirect)}` : "";
      nav(`/verificar-email?pending=1&email=${encodeURIComponent(form.email.trim())}${redirectQS}`);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Crie sua conta"
      subtitle="Cadastre-se para explorar peças exclusivas e experimentar joias virtualmente."
      footer={
        <span>
          Já tem conta?{" "}
          <Link
            to={`/login${redirect !== "/" ? `?redirect=${encodeURIComponent(redirect)}` : ""}`}
            className="auth-link"
            data-testid="link-to-login"
          >
            Entrar
          </Link>
        </span>
      }
    >
      <button
        type="button"
        onClick={() => login(redirect)}
        className="auth-cta auth-cta-secondary mb-2"
        data-testid="register-google-btn"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M22.5 12.27c0-.78-.07-1.53-.2-2.27H12v4.51h5.92c-.25 1.37-1 2.53-2.13 3.31v2.75h3.44c2.02-1.86 3.18-4.6 3.18-7.86z" fill="#4285F4"/>
          <path d="M12 23c2.88 0 5.3-.95 7.07-2.58l-3.44-2.67c-.95.64-2.17 1.02-3.63 1.02-2.79 0-5.16-1.88-6-4.42H2.43v2.78A10.97 10.97 0 0 0 12 23z" fill="#34A853"/>
          <path d="M6 14.36a6.6 6.6 0 0 1 0-4.21V7.36H2.43A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.95L6 14.36z" fill="#FBBC05"/>
          <path d="M12 5.4c1.57 0 2.98.54 4.09 1.6l3.06-3.06C17.3 2.18 14.88 1.2 12 1.2A10.97 10.97 0 0 0 2.43 7.36L6 10.15c.84-2.54 3.21-4.75 6-4.75z" fill="#EA4335"/>
        </svg>
        Cadastrar com Google
      </button>

      <div className="auth-divider">ou cadastro manual</div>

      <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
        <div>
          <label className="auth-label" htmlFor="name">Nome completo</label>
          <input id="name" type="text" required autoComplete="name"
            value={form.name} onChange={set("name")}
            className="auth-input" data-testid="register-name-input"/>
        </div>
        <div>
          <label className="auth-label" htmlFor="email">E-mail</label>
          <input id="email" type="email" required autoComplete="email"
            value={form.email} onChange={set("email")}
            className="auth-input" data-testid="register-email-input"/>
        </div>
        <div>
          <label className="auth-label" htmlFor="phone">Telefone</label>
          <input id="phone" type="tel" required autoComplete="tel"
            value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })}
            placeholder="(11) 99999-0000"
            className="auth-input" data-testid="register-phone-input"/>
        </div>
        <div>
          <label className="auth-label" htmlFor="cpf">CPF</label>
          <input id="cpf" type="text" required inputMode="numeric"
            value={form.cpf} onChange={(e) => setForm({ ...form, cpf: maskCpf(e.target.value) })}
            placeholder="000.000.000-00"
            className="auth-input" data-testid="register-cpf-input"/>
        </div>
        <div>
          <label className="auth-label" htmlFor="pw">Senha (mín. 8 caracteres)</label>
          <input id="pw" type="password" required autoComplete="new-password" minLength={8}
            value={form.password} onChange={set("password")}
            className="auth-input" data-testid="register-password-input"/>
        </div>
        <div>
          <label className="auth-label" htmlFor="pw2">Confirmar senha</label>
          <input id="pw2" type="password" required autoComplete="new-password" minLength={8}
            value={form.confirm} onChange={set("confirm")}
            className="auth-input" data-testid="register-confirm-input"/>
        </div>

        {error && (
          <p className="text-sm text-[#B85050]" data-testid="register-error">{error}</p>
        )}

        {/* LGPD consent — opens the policy in an overlaid modal so the
            visitor never leaves the form and doesn't lose what they typed. */}
        <label className="flex items-start gap-3 text-sm cursor-pointer select-none" data-testid="privacy-consent-row">
          <input
            type="checkbox"
            checked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#B8A88A]"
            data-testid="privacy-consent-checkbox"
            required
          />
          <span className="text-[#4A4A4A] leading-snug">
            Li e aceito a{" "}
            <button
              type="button"
              onClick={() => setShowPrivacyModal(true)}
              className="auth-link underline-offset-4 underline"
              data-testid="open-privacy-policy-btn"
            >
              Política de Privacidade
            </button>
            .
          </span>
        </label>

        <button
          type="submit"
          disabled={loading || !privacyAccepted}
          className="auth-cta"
          data-testid="register-submit-btn"
        >
          {loading ? "Criando…" : "Criar minha conta"}
        </button>
      </form>

      <PrivacyPolicyModal open={showPrivacyModal} onOpenChange={setShowPrivacyModal} />
    </AuthShell>
  );
}
